import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/api';

export default function ReferralApprovals() {
  const [pending,  setPending]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [confirm,  setConfirm]  = useState(null); // { id, rewardType, rewardValue }
  const [acting,   setActing]   = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [reason,   setReason]   = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/superadmin/referral-approvals');
      setPending(res.data.data.pending || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const doApprove = async () => {
    if (!confirm) return;
    setActing(true);
    try {
      await apiClient.post(`/superadmin/referral-approvals/${confirm.id}/approve`);
      setConfirm(null);
      fetchPending();
    } catch (e) {
      setError(e.response?.data?.message || 'Approve failed');
    } finally {
      setActing(false);
    }
  };

  const doReject = async () => {
    if (!rejectId) return;
    setActing(true);
    try {
      await apiClient.post(`/superadmin/referral-approvals/${rejectId}/reject`, { reason });
      setRejectId(null);
      setReason('');
      fetchPending();
    } catch (e) {
      setError(e.response?.data?.message || 'Reject failed');
    } finally {
      setActing(false);
    }
  };

  const rewardPreview = (item) => {
    const type  = item.rewardType  || '—';
    const value = item.rewardValue != null ? item.rewardValue : '—';
    return `${value} (${type})`;
  };

  return (
    <div style={S.wrap}>
      <h2 style={S.heading}>Referral Approval Queue</h2>
      {error && <p style={S.error}>{error}</p>}

      {loading ? <p>Loading…</p> : pending.length === 0 ? (
        <p style={S.empty}>No pending referral rewards. All clear.</p>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>{['Type', 'Referrer', 'Referred party', 'Reward preview', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {pending.map(item => (
              <tr key={item._id}>
                <td style={S.td}>{item.referralType}</td>
                <td style={S.td}>
                  <div style={S.name}>{item.referrerUserId?.firstName} {item.referrerUserId?.lastName}</div>
                  <div style={S.sub}>{item.referrerTenantId?.tenantName}</div>
                </td>
                <td style={S.td}>
                  <div style={S.name}>{item.registeredUserId?.firstName} {item.registeredUserId?.lastName}</div>
                  <div style={S.sub}>{item.registeredTenantId?.tenantName || item.registeredTenantId?.city}</div>
                </td>
                <td style={S.td}>{rewardPreview(item)}</td>
                <td style={S.td}>
                  <button
                    onClick={() => setConfirm({ id: item._id, rewardType: item.rewardType, rewardValue: item.rewardValue })}
                    style={S.btnGreen}
                  >Approve</button>
                  {' '}
                  <button onClick={() => { setRejectId(item._id); setReason(''); }} style={S.btnRed}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Approve ConfirmModal — CONSTRAINT 8: financial action */}
      {confirm && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>Approve referral reward?</h3>
            <p style={S.modalBody}>
              Reward type: <strong>{confirm.rewardType}</strong> · Value: <strong>{confirm.rewardValue}</strong>
            </p>
            <p style={S.modalBody}>This will issue a billing credit to the referrer's account. This action cannot be undone.</p>
            <div style={S.modalBtns}>
              <button onClick={() => setConfirm(null)} style={S.btnCancel} disabled={acting}>Cancel</button>
              <button onClick={doApprove} style={S.btnConfirm} disabled={acting}>
                {acting ? 'Approving…' : 'Confirm approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectId && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>Reject referral reward?</h3>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (optional)"
              maxLength={500}
              style={S.textarea}
            />
            <div style={S.modalBtns}>
              <button onClick={() => setRejectId(null)} style={S.btnCancel} disabled={acting}>Cancel</button>
              <button onClick={doReject} style={S.btnRed} disabled={acting}>
                {acting ? 'Rejecting…' : 'Confirm reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap:       { padding: '24px', maxWidth: '960px' },
  heading:    { fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#1a3d2b' },
  error:      { color: '#c0392b', marginBottom: '12px' },
  empty:      { color: '#6b7280', fontSize: '15px' },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { textAlign: 'left', padding: '8px 12px', background: '#f0f4f2', fontWeight: '600', fontSize: '13px', borderBottom: '1px solid #dde' },
  td:         { padding: '10px 12px', fontSize: '14px', borderBottom: '1px solid #eee', verticalAlign: 'top' },
  name:       { fontWeight: '600', color: '#1a3d2b' },
  sub:        { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  btnGreen:   { background: '#d4edda', color: '#1a6b5a', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', minHeight: '36px' },
  btnRed:     { background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', minHeight: '36px' },
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:      { background: '#fff', borderRadius: '12px', padding: '28px', width: '440px', maxWidth: '90vw' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1a3d2b', marginBottom: '12px' },
  modalBody:  { fontSize: '14px', color: '#555', marginBottom: '12px', lineHeight: '1.6' },
  modalBtns:  { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' },
  btnCancel:  { background: '#e9ecef', color: '#555', border: 'none', borderRadius: '6px', padding: '10px 20px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' },
  btnConfirm: { background: '#1a6b5a', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 20px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' },
  textarea:   { width: '100%', height: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' },
};
